// src/screens/Profile.jsx
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'intl-tel-input/build/css/intlTelInput.css';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import intlTelInput from 'intl-tel-input';

import '../css/Profile.css';
import useIdleLogout from "../hooks/useIdleLogout";

/* ============================== API CONFIG ============================== */
const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL || 'https://backend.tabdhey.bt/driver'
).replace(/\/+$/, '');

const API_BASE = `${API_BASE_URL}/api/profile`;

const DEFAULT_AVATAR = process.env.PUBLIC_URL + '/assets/img/admin profile.jpg';

const isAbsoluteUrl = (u) => /^https?:\/\//i.test(u || '');

const joinUrl = (base, path) =>
  `${String(base).replace(/\/+$/, '')}/${String(path).replace(/^\/+/, '')}`;

const resolveImageUrl = (raw) =>
  raw ? (isAbsoluteUrl(raw) ? raw : joinUrl(API_BASE_URL, raw)) : DEFAULT_AVATAR;

/* ============================== AUTH HELPERS ============================== */
function getLS(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function parseJSON(raw) {
  try {
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function readStoredUser() {
  return (
    parseJSON(getLS('user')) ||
    parseJSON(getLS('admin')) ||
    parseJSON(getLS('profile')) ||
    parseJSON(getLS('currentUser')) ||
    {}
  );
}

function getBearerToken() {
  const directToken =
    getLS('access_token') ||
    getLS('token') ||
    getLS('adminToken') ||
    getLS('accessToken') ||
    getLS('authToken') ||
    sessionStorage.getItem('access_token') ||
    sessionStorage.getItem('token') ||
    sessionStorage.getItem('adminToken') ||
    sessionStorage.getItem('accessToken') ||
    sessionStorage.getItem('authToken');

  if (directToken) return String(directToken).trim();

  const user = readStoredUser();

  return String(
    user?.token ||
    user?.adminToken ||
    user?.accessToken ||
    user?.access_token ||
    user?.authToken ||
    user?.jwt ||
    ''
  ).trim();
}

function getAuthHeaders(withJson = false) {
  const token = getBearerToken();

  const headers = {
    Accept: 'application/json',
  };

  if (withJson) {
    headers['Content-Type'] = 'application/json';
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

function getLoggedInUserId() {
  const directUserId = getLS('user_id') || getLS('admin_id');

  if (directUserId) {
    return directUserId;
  }

  const user = readStoredUser();

  return String(
    user?.user_id ??
    user?.id ??
    user?._id ??
    user?.admin_id ??
    ''
  ).trim();
}

/* --------- Countries & rules --------- */
const COUNTRY_RULES = {
  bt: { dial: '+975', national: /^(17|16|77)\d{6}$/ },
  in: { dial: '+91',  national: /^[6-9]\d{9}$/ },
  np: { dial: '+977', national: /^(97|98|96)\d{8}$/ },
  bd: { dial: '+880', national: /^1[3-9]\d{8}$/ },
  pk: { dial: '+92',  national: /^3\d{9}$/ },
  lk: { dial: '+94',  national: /^7\d{8}$/ },
  th: { dial: '+66',  national: /^(6|8|9)\d{8}$/ },
  my: { dial: '+60',  national: /^\d{9,10}$/ },
  sg: { dial: '+65',  national: /^[689]\d{7}$/ },
  id: { dial: '+62',  national: /^\d{9,11}$/ },
  ph: { dial: '+63',  national: /^9\d{9}$/ },
  vn: { dial: '+84',  national: /^(3|5|7|8|9)\d{8}$/ },
  cn: { dial: '+86',  national: /^1\d{10}$/ },
  jp: { dial: '+81',  national: /^0?\d{9,10}$/ },
  kr: { dial: '+82',  national: /^0?1\d{8,9}$/ },
  mm: { dial: '+95',  national: /^9\d{8,10}$/ },
};

const ONLY_COUNTRIES = [
  'bt',
  'in',
  'np',
  'bd',
  'pk',
  'lk',
  'th',
  'my',
  'sg',
  'id',
  'ph',
  'vn',
  'cn',
  'jp',
  'kr',
  'mm',
];

/* ===== per-country national length rules (excluding country code) ===== */
const PHONE_RULES = {
  bt: { min: 8, max: 8 },
  in: { min: 10, max: 10 },
  np: { min: 10, max: 10 },
  bd: { min: 10, max: 10 },
  pk: { min: 10, max: 10 },
  lk: { min: 9, max: 9 },
  th: { min: 9, max: 10 },
  my: { min: 8, max: 9 },
  sg: { min: 8, max: 8 },
  id: { min: 8, max: 12 },
  ph: { min: 10, max: 10 },
  vn: { min: 9, max: 10 },
  cn: { min: 11, max: 11 },
  jp: { min: 10, max: 10 },
  kr: { min: 9, max: 10 },
  mm: { min: 7, max: 10 },
};

const COUNTRY_LABELS = {
  bt: 'Bhutan',
  in: 'India',
  np: 'Nepal',
  bd: 'Bangladesh',
  pk: 'Pakistan',
  lk: 'Sri Lanka',
  th: 'Thailand',
  my: 'Malaysia',
  sg: 'Singapore',
  id: 'Indonesia',
  ph: 'Philippines',
  vn: 'Vietnam',
  cn: 'China',
  jp: 'Japan',
  kr: 'South Korea',
  mm: 'Myanmar',
};

/* --------- Dial helpers --------- */
const DIAL_TO_ISO = Object.fromEntries(
  Object.entries(COUNTRY_RULES).map(([iso2, v]) => [
    String(v.dial).replace(/\D/g, ''),
    iso2,
  ])
);

const pickIsoByDial = (onlyDigits) => {
  let best = null;

  for (const [dialDigits, iso2] of Object.entries(DIAL_TO_ISO)) {
    if (onlyDigits.startsWith(dialDigits)) {
      if (!best || dialDigits.length > best.dial.length) {
        best = { dial: dialDigits, iso2 };
      }
    }
  }

  return best;
};

/* utils */
function waitForUtils(maxMs = 2500, interval = 60) {
  return new Promise((resolve) => {
    const t0 = Date.now();

    (function tick() {
      if (window.intlTelInputUtils) return resolve(true);
      if (Date.now() - t0 >= maxMs) return resolve(false);
      setTimeout(tick, interval);
    })();
  });
}

/* console helpers */
const cLabel = (t) => [`%c${t}`, 'color:#6c5ce7;font-weight:600'];

const fmtBytes = (n) => {
  if (!Number.isFinite(n)) return `${n}`;

  const u = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let v = n;

  while (v >= 1024 && i < u.length - 1) {
    v /= 1024;
    i++;
  }

  return `${v.toFixed(1)} ${u[i]}`;
};

const preflightLog = ({ url, username, email, phone, file, selected }) => {
  console.groupCollapsed(...cLabel('PRE-FETCH (exactly what will be sent)'));
  console.log('URL:', url);
  console.table([
    { field: 'username', value: username },
    { field: 'email', value: email },
    { field: 'phone', value: phone || '(not sent)' },
    {
      field: 'image',
      value: file
        ? `${file.name} • ${fmtBytes(file.size)} • ${file.type || 'n/a'}`
        : '(not sent)',
    },
    { field: 'selectedCountry', value: selected?.iso2 || '(none)' },
    { field: 'selectedDial', value: selected?.dialCode || '(none)' },
  ]);
  console.groupEnd();
};

function formDataPreview(fd) {
  const out = [];

  for (const [k, v] of fd.entries()) {
    if (v instanceof File || v instanceof Blob) {
      out.push({
        key: k,
        value: `[${v.constructor.name}] ${v.name} • ${fmtBytes(v.size)} • ${v.type || 'n/a'}`,
      });
    } else {
      out.push({ key: k, value: String(v) });
    }
  }

  return out;
}

export default function Profile() {
  useIdleLogout();

  const [profile, setProfile] = useState(null);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // 🔐 change-password states
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPwd, setChangingPwd] = useState(false);

  // separate "saving" so we don't remount the whole page on validation errors
  const [saving, setSaving] = useState(false);

  const token = getBearerToken();
  const userId = getLoggedInUserId();

  // phone input
  const phoneInputRef = useRef(null);
  const itiRef = useRef(null);
  const initedRef = useRef(false);

  /* -------- fetch profile -------- */
  useEffect(() => {
    (async () => {
      if (!userId) {
        setLoading(false);
        setError('User ID not found. Please log in again.');
        return;
      }

      if (!token) {
        setLoading(false);
        setError('Access token not found. Please log in again.');
        return;
      }

      try {
        const res = await fetch(`${API_BASE}/${userId}`, {
          method: 'GET',
          headers: getAuthHeaders(false),
          cache: 'no-store',
        });

        if (!res.ok) {
          const txt = await res.text().catch(() => '');
          throw new Error(`Failed: ${res.status}${txt ? ` - ${txt}` : ''}`);
        }

        const json = await res.json();
        const d = json.data || json;

        setProfile(d);
        setUsername(d.user_name || '');
        setEmail(d.email || '');
      } catch (err) {
        console.error('GET profile failed', err);
        setError('Could not load profile. Please login again or check backend.');
        Swal.fire('Error', err.message || 'Could not load profile', 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, [userId, token]);

  /* -------- init intl-tel-input -------- */
  useLayoutEffect(() => {
    if (loading || !phoneInputRef.current || initedRef.current) return;

    const el = phoneInputRef.current;
    initedRef.current = true;

    // show dial code separately; user types NATIONAL digits only
    itiRef.current = intlTelInput(el, {
      initialCountry: 'bt',
      onlyCountries: ONLY_COUNTRIES,
      nationalMode: true,
      separateDialCode: true,
      autoPlaceholder: 'polite',
      formatOnDisplay: true,
      allowDropdown: true,
      utilsScript: 'https://cdn.jsdelivr.net/npm/intl-tel-input@18.5.6/build/js/utils.js',
    });

    return () => {
      try {
        itiRef.current?.destroy();
      } catch {}

      itiRef.current = null;
      initedRef.current = false;
    };
  }, [loading]);

  /* -------- prefill phone when ready -------- */
  useEffect(() => {
    if (!profile || !itiRef.current) return;

    const phoneRaw = `${profile.phone ?? ''}`.trim();

    try {
      if (phoneRaw) {
        if (!phoneRaw.startsWith('+') && /^(17|16|77)\d{6}$/.test(phoneRaw)) {
          itiRef.current.setCountry('bt');
          phoneInputRef.current.value = phoneRaw; // national digits
        } else {
          itiRef.current.setNumber(phoneRaw); // E.164 or others
        }
      }
    } catch (e) {
      console.log('Prefill error:', e);

      if (!phoneRaw.startsWith('+')) {
        phoneInputRef.current.value = phoneRaw;
      }
    }
  }, [profile]);

  /* -------- phone facts -------- */
  async function buildFacts(iti) {
    // capture raw BEFORE any await/tooling so user’s digits don’t “disappear”
    const rawInitial = (phoneInputRef.current?.value || '').trim();
    const selected = iti.getSelectedCountryData() || {};

    const utilsReady = await waitForUtils();

    let e164 = '';
    let validByLib = false;

    // try plugin first
    if (utilsReady && window.intlTelInputUtils) {
      try {
        e164 = iti.getNumber(window.intlTelInputUtils.numberFormat.E164) || '';
        validByLib = iti.isValidNumber();
      } catch {}
    }

    // fallback to selected dial + raw national
    const natDigitsRaw = rawInitial.replace(/\D/g, '');

    if (!e164) {
      const dial = String(selected.dialCode || '');

      if (dial && natDigitsRaw) {
        e164 = `+${dial}${natDigitsRaw}`;
      }
    }

    const onlyDigits = (e164 || '').replace(/\D/g, '');
    const picked = pickIsoByDial(onlyDigits) || {
      iso2: (selected.iso2 || '').toLowerCase(),
      dial: String(selected.dialCode || ''),
    };

    const dialForNat = picked?.dial || '';
    const nationalDigits = dialForNat ? onlyDigits.slice(dialForNat.length) : onlyDigits;

    // loud facts
    console.groupCollapsed(...cLabel('PHONE FACTS (derived)'));
    console.table([
      { key: 'rawInitial', value: rawInitial || '(empty)' },
      { key: 'utilsReady', value: String(utilsReady) },
      { key: 'selectedCountry', value: selected?.iso2 || '(none)' },
      { key: 'selectedDial', value: selected?.dialCode || '(none)' },
      { key: 'e164', value: e164 || '(none)' },
      { key: 'validByLib', value: String(validByLib) },
      { key: 'onlyDigits', value: onlyDigits || '(none)' },
      { key: 'useIso2', value: picked?.iso2 || '(none)' },
      { key: 'nationalDigits', value: nationalDigits || '(none)' },
    ]);
    console.groupEnd();

    return {
      utilsReady,
      raw: rawInitial,
      e164,
      validByLib,
      onlyDigits,
      useIso2: (picked?.iso2 || '').toLowerCase(),
      nationalDigits,
      selectedCountry: selected?.iso2 || '',
      selectedDial: selected?.dialCode || '',
    };
  }

  /* -------- validator -------- */
  function validatePerCountry(f) {
    const { validByLib, e164, onlyDigits, useIso2, nationalDigits } = f;

    if (!onlyDigits) {
      return { ok: false, msg: 'Please enter phone number.' };
    }

    const ruleLen = PHONE_RULES[useIso2];
    const label = COUNTRY_LABELS[useIso2] || 'this country';

    // Enforce min/max NATIONAL digits (excluding country code)
    if (ruleLen) {
      const len = nationalDigits.length;

      if (len < ruleLen.min || len > ruleLen.max) {
        const expected =
          ruleLen.min === ruleLen.max
            ? `${ruleLen.min} digits`
            : `${ruleLen.min}–${ruleLen.max} digits`;

        return {
          ok: false,
          msg: `Phone number must have ${expected} (excluding country code) for ${label}. You entered ${len}.`,
        };
      }
    } else if (nationalDigits.length < 6) {
      return { ok: false, msg: `Please enter a valid phone number for ${label}.` };
    }

    // Bhutan extra prefix rule
    if (useIso2 === 'bt') {
      if (!/^(17|16|77)\d{6}$/.test(nationalDigits)) {
        return {
          ok: false,
          msg: 'For Bhutan: number must be 8 digits and start with 17, 16, or 77.',
        };
      }
    }

    // Apply COUNTRY_RULES regex for extra sanity
    const rule = COUNTRY_RULES[useIso2];

    if (rule && !rule.national.test(nationalDigits)) {
      return {
        ok: false,
        msg: `Please enter a valid phone number for ${label}.`,
      };
    }

    // If libphonenumber says it's valid and e164 shape looks fine, trust it
    if (validByLib && /^\+\d{8,15}$/.test(e164)) {
      return { ok: true };
    }

    // Final generic guard on total length
    if (onlyDigits.length < 8 || onlyDigits.length > 15) {
      return { ok: false, msg: 'Please enter a valid phone number.' };
    }

    return { ok: true };
  }

  /* -------- Save profile -------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); // only affect button, not whole page

    try {
      if (!token) {
        throw new Error('Access token not found. Please log in again.');
      }

      const iti = itiRef.current;

      if (!iti) {
        throw new Error('Phone input not ready.');
      }

      // snapshot of typed values
      console.groupCollapsed(...cLabel('INPUT SNAPSHOT (before validation)'));
      console.table([
        { field: 'username', value: username },
        { field: 'email', value: email },
        {
          field: 'phoneField',
          value: (phoneInputRef.current?.value || '').trim() || '(empty)',
        },
        {
          field: 'image',
          value: file
            ? `${file.name} • ${fmtBytes(file.size)} • ${file.type || 'n/a'}`
            : '(none)',
        },
      ]);
      console.groupEnd();

      const facts = await buildFacts(iti);
      const verdict = validatePerCountry(facts);

      console.groupCollapsed(...cLabel('VALIDATION VERDICT'));
      console.table([{ ok: verdict.ok, msg: verdict.msg || '(none)' }]);
      console.groupEnd();

      if (!verdict.ok) {
        // tell admin to correct and keep whatever they typed
        throw new Error(verdict.msg);
      }

      const finalE164 =
        facts.e164 && /^\+\d{8,15}$/.test(facts.e164)
          ? facts.e164
          : `+${facts.onlyDigits}`;

      // preflight print
      preflightLog({
        url: `${API_BASE}/${userId}`,
        username,
        email,
        phone: finalE164,
        file,
        selected: {
          iso2: facts.selectedCountry,
          dialCode: facts.selectedDial,
        },
      });

      const formData = new FormData();
      formData.append('user_name', username);
      formData.append('email', email);
      formData.append('phone', finalE164);

      if (file) {
        formData.append('profile_image', file);
      }

      console.groupCollapsed(...cLabel('FormData entries'));
      console.table(formDataPreview(formData));
      console.groupEnd();

      const res = await fetch(`${API_BASE}/${userId}`, {
        method: 'PUT',
        headers: getAuthHeaders(false),
        body: formData,
      });

      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        console.error('Server error', res.status, txt);
        throw new Error(`Server ${res.status}: ${txt || 'Profile update failed'}`);
      }

      const json = await res.json();
      const updated = json.data || json;

      setProfile(updated);

      await Swal.fire('Success', 'Profile updated successfully!', 'success');
      window.location.reload();
    } catch (err) {
      console.error(err);
      await Swal.fire('Error', err.message || 'Could not update profile.', 'error');
      // NOTE: we do NOT touch phoneInputRef.value here
    } finally {
      setSaving(false);
    }
  };

  // 🔐 Change password
  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (!currentPassword || !newPassword || !confirmPassword) {
      Swal.fire('Error', 'Please fill all password fields.', 'error');
      return;
    }

    const strongPasswordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

    if (!strongPasswordRegex.test(newPassword)) {
      Swal.fire(
        'Weak Password',
        'Password must be at least 8 characters long and include uppercase, lowercase, number, and special character.',
        'error'
      );
      return;
    }

    if (newPassword !== confirmPassword) {
      Swal.fire('Error', 'New passwords do not match.', 'error');
      return;
    }

    try {
      if (!token) {
        throw new Error('Access token not found. Please log in again.');
      }

      setChangingPwd(true);

      const PASSWORD_API = `${API_BASE}/password/${userId}`;

      const res = await fetch(PASSWORD_API, {
        method: 'PUT',
        headers: getAuthHeaders(true),
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
        }),
      });

      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        console.error('Password change failed:', res.status, txt);
        throw new Error(txt || `Server ${res.status}`);
      }

      Swal.fire('Updated', 'Password changed successfully.', 'success');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      console.error('Change password failed', err);
      Swal.fire('Error', err.message || 'Could not change password.', 'error');
    } finally {
      setChangingPwd(false);
    }
  };

  if (loading) {
    return (
      <div className="profile-page main-content">
        <div className="text-center mt-5">Loading profile…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="profile-page main-content">
        <div className="text-center mt-5 text-danger">{error}</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="profile-page main-content">
        <div className="text-center mt-5">No profile found.</div>
      </div>
    );
  }

  return (
    <div className="profile-page main-content">
      <div className="card profile-card mb-4">
        <div className="card-body">
          <h5 className="card-title mb-4">Admin Profile</h5>

          <div className="mb-4 text-center">
            <img
              src={
                file
                  ? URL.createObjectURL(file)
                  : profile.profile_image
                    ? resolveImageUrl(profile.profile_image)
                    : DEFAULT_AVATAR
              }
              alt="Profile"
              className="rounded-circle profile-preview"
              onError={(e) => {
                if (e.currentTarget.src !== DEFAULT_AVATAR) {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = DEFAULT_AVATAR;
                }
              }}
            />

            <h5 className="profile-username">{username}</h5>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label className="form-label">Upload New Photo</label>
              <input
                type="file"
                className="form-control"
                accept="image/*"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Username</label>
              <input
                type="text"
                className="form-control"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Email</label>
              <input
                type="email"
                className="form-control"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            {/* Phone input (dial shows in flag area; type just national digits) */}
            <div className="mb-3">
              <label className="form-label">Phone</label>
              <input
                ref={phoneInputRef}
                type="tel"
                id="profilePhone"
                name="profilePhone"
                className="form-control pill form-control--phone"
                placeholder="17554113"
                autoComplete="tel"
                inputMode="tel"
                aria-label="Phone number"
                spellCheck={false}
              />
            </div>

            <button type="submit" className="btn btn-primary w-100" disabled={saving}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </form>
        </div>
      </div>

      {/* Change Password */}
      <div className="card profile-card mb-4">
        <div className="card-body">
          <h5 className="card-title mb-3">Change Password</h5>

          <form onSubmit={handleChangePassword}>
            <div className="mb-3">
              <label htmlFor="currentPassword" className="form-label">
                Current Password
              </label>

              <input
                id="currentPassword"
                type="password"
                className="form-control"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
              />
            </div>

            <div className="mb-3">
              <label htmlFor="newPassword" className="form-label">
                New Password
              </label>

              <input
                id="newPassword"
                type="password"
                className="form-control"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
              />
            </div>

            <div className="mb-4">
              <label htmlFor="confirmPassword" className="form-label">
                Confirm New Password
              </label>

              <input
                id="confirmPassword"
                type="password"
                className="form-control"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
              />
            </div>

            <button type="submit" className="btn btn-primary w-100" disabled={changingPwd}>
              {changingPwd ? 'Updating…' : 'Update Password'}
            </button>
          </form>
        </div>
      </div>

      <footer className="profile-footer">
        <small>
          © {new Date().getFullYear()} <strong>སྟབས་བདེ།</strong> Admin • All rights reserved
        </small>
      </footer>
    </div>
  );
}
